import { Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import MiniCalendar from '../MiniCalendar.js';
import PdfFooter from '../PdfFooter.js';
import { headerVariablesContext } from './index.js';

/**
 * Converts a flat, ordered list of layout items into an explicit row structure
 * suitable for deterministic PDF rendering.
 *
 * <p>The function groups calendar items into rows of a fixed column count and
 * ensures that title items always begin a new logical section. When enabled,
 * a title is grouped together with the first calendar row that follows it,
 * allowing both to be rendered atomically (for example using {@code wrap={false}}
 * in react-pdf) so they never split across page boundaries.</p>
 *
 * <p>Calendar rows always contain exactly {@code columnCount} cells. If fewer
 * calendar items are available for a row, the remaining cells are padded with
 * {@code null} values to preserve column alignment and spacing.</p>
 *
 * <h3>Row types produced</h3>
 * <ul>
 *   <li>{@code { type: 'title', item }} –
 *       A standalone title row occupying the full width.</li>
 *   <li>{@code { type: 'calendarRow', items, isLastRow }} –
 *       A row containing exactly {@code columnCount} calendar cells, padded
 *       with {@code null} where necessary. The {@code isLastRow} flag indicates
 *       whether this row is the final calendar row of its logical section.</li>
 *   <li>{@code { type: 'titleWithCalendarRow', title, items, isLastRow }} –
 *       A title grouped atomically with its first calendar row. The
 *       {@code isLastRow} flag indicates whether this row is the final calendar
 *       row of its logical section.</li>
 * </ul>
 *
 * <h3>Behavior guarantees</h3>
 * <ul>
 *   <li>Titles always start a new logical section and never appear after
 *       calendar rows.</li>
 *   <li>The relative order of input items is preserved.</li>
 *   <li>No calendar row exceeds {@code columnCount} items.</li>
 *   <li>Partial calendar rows are padded exactly once and never reprocessed.</li>
 *   <li>If enabled, a title and its first calendar row are emitted together,
 *       even if that row is incomplete.</li>
 *   <li>All calendar-bearing rows expose an {@code isLastRow} boolean property.</li>
 *   <li>Exactly one calendar-bearing row per logical section has
 *       {@code isLastRow = true}.</li>
 *   <li>A logical section ends immediately before the next title, before the
 *       next title-with-calendar row, or at the end of the input data.</li>
 *   <li>No infinite loops or re-entrant flushing can occur.</li>
 * </ul>
 *
 * @param data
 *     The flat, ordered list of layout items. Each item must expose a
 *     {@code type} property (for example {@code 'title'} or {@code 'calendar'}).
 *
 * @param columnCount
 *     The number of columns per calendar row. Must be a positive integer.
 *
 * @param keepTitleWithFirstRow
 *     When {@code true}, a title is grouped with the first calendar row that
 *     follows it and emitted as a single atomic structure. When {@code false},
 *     titles and calendar rows are emitted independently.
 *
 * @return
 *     An ordered list of row descriptors that explicitly define the layout
 *     structure and can be rendered deterministically for PDF output.
 */
function buildRows(data, columnCount, keepTitleWithFirstRow) {
    const rows = [];

    let pendingTitle = null;
    let buffer = [];

    const padToColumns = (items) => {
        const padded = items.slice();
        while (padded.length < columnCount) {
            padded.push(null);
        }
        return padded;
    };

    const emitCalendarRow = (items) => {
        rows.push({ type: 'calendarRow', items: padToColumns(items) });
    };

    const flushStandaloneCalendars = () => {
        while (buffer.length > 0) {
            const chunk = buffer.splice(0, columnCount);
            emitCalendarRow(chunk);
        }
    };

    const flushPendingTitleSection = () => {
        if (!pendingTitle) {
            return;
        }

        if (keepTitleWithFirstRow) {
            if (buffer.length > 0) {
                const firstRow = buffer.splice(0, columnCount);
                rows.push({
                    type: 'titleWithCalendarRow',
                    title: pendingTitle,
                    items: padToColumns(firstRow),
                });
                pendingTitle = null;

                // remaining calendars after the first row become normal rows
                flushStandaloneCalendars();
                return;
            }

            // title with no calendars following
            rows.push({ type: 'title', item: pendingTitle });
            pendingTitle = null;
            return;
        }

        // keepTitleWithFirstRow = false
        rows.push({ type: 'title', item: pendingTitle });
        pendingTitle = null;
    };

    for (const item of data) {
        if (item.type === 'title') {
            // Close out the previous section (title + its calendars) before starting a new title
            flushPendingTitleSection();

            // If there was no pending title, any buffered calendars are standalone
            if (!pendingTitle && buffer.length > 0) {
                flushStandaloneCalendars();
            }

            pendingTitle = item;
            continue;
        }

        // calendar item
        buffer.push(item);

        // If we can complete the title+firstRow group with a full row, emit it immediately
        if (pendingTitle && keepTitleWithFirstRow && buffer.length === columnCount) {
            rows.push({
                type: 'titleWithCalendarRow',
                title: pendingTitle,
                items: padToColumns(buffer),
            });
            pendingTitle = null;
            buffer = [];
            continue;
        }

        // Normal calendar rows (only when not waiting on a title)
        if (!pendingTitle && buffer.length === columnCount) {
            emitCalendarRow(buffer);
            buffer = [];
        }
    }

    // End of data: close out whatever is left
    flushPendingTitleSection();
    flushStandaloneCalendars();

    // ---- POST-PROCESSING: SECTION / DOCUMENT FLAGS ----

    // Initialize defaults
    for (const row of rows) {
        if (row.type === 'calendarRow' || row.type === 'titleWithCalendarRow') {
            row.isFirstRowInSection = false;
            row.isLastRowInSection = false;
            row.isLastRowInDocument = false;
        }
    }

    let firstCalendarRowInSection = null;
    let lastCalendarRowInSection = null;
    let lastCalendarRowInDocument = null;

    for (const row of rows) {
        const startsNewSection =
            row.type === 'title' || row.type === 'titleWithCalendarRow';

        if (startsNewSection) {
            // Close previous section
            if (lastCalendarRowInSection) {
                lastCalendarRowInSection.isLastRowInSection = true;
            }

            firstCalendarRowInSection = null;
            lastCalendarRowInSection = null;
        }

        if (row.type === 'calendarRow' || row.type === 'titleWithCalendarRow') {
            if (!firstCalendarRowInSection) {
                firstCalendarRowInSection = row;
                row.isFirstRowInSection = true;
            }

            lastCalendarRowInSection = row;
            lastCalendarRowInDocument = row;
        }
    }

    // Close final section
    if (lastCalendarRowInSection) {
        lastCalendarRowInSection.isLastRowInSection = true;
    }

    // Mark document end
    if (lastCalendarRowInDocument) {
        lastCalendarRowInDocument.isLastRowInDocument = true;
    }

    // --------------------------------------------------

    return rows;
}

/**
 * Builds a flat list of layout rows (titles and calendar cells) from a sequence
 * of year/month items.
 *
 * The function groups items into logical sections. When
 * {@code newYearsCreateNewSection} is enabled, a change of year starts a new
 * section and inserts a title row. Title rows collect all unique years that
 * occur within their section and expose them via the {@code years} property.
 *
 * @param {Array<Object>} yearMonths
 *        Ordered list of input items. Each item must expose a {@code year}
 *        property.
 * @param {boolean} newYearsCreateNewSection
 *        If {@code true}, a year change starts a new section and inserts a
 *        title row.
 *
 * @returns {Array<Object>}
 *          Flat list of rows in render order. Each row is one of:
 *          <ul>
 *              <li>{@code { type: 'title', item, years: number[] }}</li>
 *              <li>{@code { type: 'calendar', item }}</li>
 *          </ul>
 */
const buildPages = (yearMonths, newYearsCreateNewSection) => {
    const rows = [];
    let prevYear = null;

    let currentSectionYears = [];
    let currentTitleRowIndex = null;

    const finalizeSectionYears = () => {
        if (currentTitleRowIndex === null) {
            return;
        }

        rows[currentTitleRowIndex].years =
            Array.from(new Set(currentSectionYears));
    };

    yearMonths.forEach((item) => {
        const isNewYear = prevYear === null || prevYear !== item.year;

        if (newYearsCreateNewSection && isNewYear) {
            finalizeSectionYears();

            currentSectionYears = [];
            currentTitleRowIndex = rows.length;

            rows.push({
                type: 'title',
                item,
                years: [],
            });
        }

        rows.push({ type: 'calendar', item });
        currentSectionYears.push(item.year);

        prevYear = item.year;
    });

    finalizeSectionYears();

    return rows;
};

const mmToPx = (mm) => (mm * 72) / 25.4;

const cellDecorationsForMonth = (customMarkings, year, month) => {
    const datePrefix = `${year}-${month <= 9 ? '0' : ''}${month}-`;

    return Object.fromEntries(
        customMarkings
            .filter((cm) => cm.date.startsWith(datePrefix))
            .map(cm => [parseInt(cm.date.substring(8), 10), cm.cellDecorations])
    );
}

const CalendarRow = ({ items, columnCount, customMarkings, miniCalendarAspectRatio, fontSize, prependZero, startWeekOnSundays, showWeekColumn, themeName, gapBetweenColumnsInMm, contentWidthInMm, marginTopInMm = '5mm', marginBottomInMm = '5mm', debug = true }) => {
    const totalGap = gapBetweenColumnsInMm * (columnCount - 1);
    const columnWidth = (contentWidthInMm - totalGap) / columnCount;

    const calendarWidthInPx = mmToPx(columnWidth);
    const calendarHeightInPx = calendarWidthInPx / miniCalendarAspectRatio;

    const styles = StyleSheet.create({
        row: {
            flexDirection: 'row',
            width: '100%',
            paddingRight: `${gapBetweenColumnsInMm}mm`, // absorbs trailing gap
            marginTop: marginTopInMm,
            marginBottom: marginBottomInMm,
            backgroundColor: debug ? 'yellow' : null
        },
        cell: {
            flexGrow: 0,
            flexShrink: 0,
            width: `${columnWidth}mm`,
        },
        cellGap: {
            marginRight: `${gapBetweenColumnsInMm}mm`,
        },
        calendarBox: {
            width: '100%',
            height: `${calendarHeightInPx}`,
        },
        placeholder: {
            width: '100%',
            height: `${calendarHeightInPx}`,
        },
    });

    return (
        <View wrap={false} style={styles.row}>
            {items.map((cell, colIndex) => {
                const isLastColumn = colIndex === items.length - 1;

                return (
                    <View
                        key={colIndex}
                        style={[
                            styles.cell,
                            !isLastColumn && styles.cellGap,
                        ]}
                    >
                        {cell ? (
                            <View style={styles.calendarBox}>
                                <MiniCalendar
                                    width={calendarWidthInPx}
                                    height={calendarHeightInPx}
                                    year={cell.item.year} month={cell.item.month}
                                    prependZero={prependZero}
                                    startOfWeekDay={startWeekOnSundays ? 0 : 1}
                                    isoWeekColumnMode={showWeekColumn ? 'fixed' : 'none'}
                                    themeName={themeName}
                                    fontSize={fontSize}
                                    cellDecorations={cellDecorationsForMonth(customMarkings, cell.item.year, cell.item.month)} />
                            </View>
                        ) : (
                            <View style={styles.placeholder} />
                        )}
                    </View>
                );
            })}
        </View>
    );
};

export const YearMonthsAutoLayout = (props) => {
    const DEBUG = false;

    const {
        document,
        yearMonths,
        templateParser,
        sectionTitleTemplateParser,
        orientation,
        xMax,
        yMax,
        customMarkings,
        prependZero,
        startWeekOnSundays,
        themeName,
        pageSize,
        titleMarginVertical,
        contentMarginLeft,
        contentMarginRight,
        miniCalendarWidth,
        miniCalendarHeight,
        fontSize,
        miniCalendarMarginRight,
        miniCalendarMarginBottom,
        footerLeftMargin,
        footerRightMargin,
        footerBottomMargin,
        footerFontSize,
        showWeekColumn,
        ...rest
    } = props;

    if (DEBUG) {
        console.log('rest', rest);
    }

    const headerHeightInCm = document?.header?.visible ? 1 : 0;
    const footerHeightInCm = 0.4;
    const paddingLeftInCm = document?.marginsInMm?.left ? document?.marginsInMm?.left / 10 : 2;
    const paddingRightInCm = document?.marginsInMm?.right ? document?.marginsInMm?.right / 10 : 2;
    const headerTopPaddingInCm = document?.marginsInMm?.top ? document?.marginsInMm?.top / 10 : 1;
    const footerBottomPaddingInCm = document?.marginsInMm?.bottom ? document?.marginsInMm?.bottom / 10 : 1;
    const headerContentMarginInCm = 0.1;
    const footerContentMarginInCm = 0.1;
    const CONTENT_WIDTH_MM = 210 - ((paddingLeftInCm + paddingRightInCm) * 10); // A4 − left/right padding
    const columnCount = 3;
    const GAP_BETWEEN_COLUMNS_IN_MM = 5;

    const titleAlignMap = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end'
    };

    const styles = StyleSheet.create({
        page: {
            paddingTop: `${headerHeightInCm + headerTopPaddingInCm + headerContentMarginInCm}cm`,
            paddingBottom: `${footerHeightInCm + footerBottomPaddingInCm + footerContentMarginInCm}cm`,
            paddingLeft: `${paddingLeftInCm}cm`,
            paddingRight: `${paddingRightInCm}cm`
        },
        header: {
            position: 'absolute',
            top: `${headerTopPaddingInCm}cm`,
            left: `${paddingLeftInCm}cm`,
            right: `${paddingRightInCm}cm`,
            height: `${headerHeightInCm}cm`,
            fontSize: footerFontSize,
            backgroundColor: DEBUG ? 'green' : null,
            alignItems: titleAlignMap[document?.header?.titleAlignment]
        },
        footer: {
            position: 'absolute',
            bottom: `${footerBottomPaddingInCm}cm`,
            left: `${paddingLeftInCm}cm`,
            right: `${paddingRightInCm}cm`,
            height: `${footerHeightInCm}cm`,
            fontSize: footerFontSize,
            backgroundColor: DEBUG ? 'red' : null,
            alignItems: titleAlignMap['left']
        },
        content: {
            width: '100%',
        },

        titleRow: {
            width: '100%',
            // no margin on this element (it is all handled by the actual rows see marginTopInMm and marginBottomInMm)
            alignItems: titleAlignMap[document?.sectionTitle?.titleAlignment]
        },

        calendarItem: {
            flexGrow: 1,
        },

        calendarItemWithGap: {
            marginRight: '5mm',
        },
    });

    const calendarAspectRatio = miniCalendarWidth / miniCalendarHeight;

    const a = buildPages(yearMonths, true);
    const rows = buildRows(a, columnCount, true);

    if (DEBUG) {
        console.log("header height " + headerHeightInCm);
        console.log(rows);
        console.log("document config", document);
    }

    return (
        <Page size="A4" style={styles.page}>
            {/* Header */}
            {document?.header?.visible && <View style={styles.header} fixed>
                <Text>{templateParser(headerVariablesContext, document?.header?.title ?? '')}</Text>
            </View>}

            {/* Footer */}
            <View style={styles.footer} fixed>
                <Text
                    render={({ pageNumber, totalPages }) =>
                        `Page ${pageNumber} of ${totalPages}`
                    }
                />
                <PdfFooter leftMargin={footerLeftMargin} rightMargin={0} bottomMargin={0} fontSize={footerFontSize} />
            </View>

            {document?.title?.visible && <View wrap={false} style={{ fontSize: document?.title?.titleSize ?? 20, alignItems: titleAlignMap[document?.title?.titleAlignment] }}>
                <Text>{templateParser(headerVariablesContext, document?.title?.title ?? '')}</Text>
            </View>}

            <View style={styles.content}>
                {rows.map((row, rowIndex) => {
                    const marginTopInMm = `${row.isFirstRowInSection ? (rest.firstRowMarginTopInMm ?? 1) : 0}mm`;
                    const marginBottomInMm = `${row.isLastRowInDocument ? 0 : (row.isLastRowInSection ? rest.lastRowInSectionMarginBottomInMm : rest.marginBottomInMm)}mm`;

                    if (document?.sectionTitle?.visible && row.type === 'title') {
                        return (
                            <View key={rowIndex} wrap={false} style={styles.titleRow}>
                                <Text>{sectionTitleTemplateParser(row.title.years)}</Text>
                            </View>
                        );
                    }

                    if (document?.sectionTitle?.visible && row.type === 'titleWithCalendarRow') {
                        return (
                            <View key={rowIndex} wrap={false} style={{ backgroundColor: DEBUG ? 'orange' : null }}>
                                <View style={[styles.titleRow, { backgroundColor: DEBUG ? 'green' : null }]}>
                                    <Text>{sectionTitleTemplateParser(row.title.years)}</Text>
                                </View>

                                <CalendarRow
                                    debug={DEBUG}
                                    items={row.items}
                                    columnCount={columnCount}
                                    customMarkings={customMarkings}
                                    miniCalendarAspectRatio={calendarAspectRatio}
                                    fontSize={fontSize}
                                    prependZero={prependZero}
                                    startWeekOnSundays={startWeekOnSundays}
                                    showWeekColumn={showWeekColumn}
                                    themeName={themeName}
                                    gapBetweenColumnsInMm={GAP_BETWEEN_COLUMNS_IN_MM}
                                    contentWidthInMm={CONTENT_WIDTH_MM}
                                    marginTopInMm={marginTopInMm}
                                    marginBottomInMm={marginBottomInMm}
                                />
                            </View>
                        );
                    }

                    if (row.type === 'calendarRow') {
                        return (

                            <CalendarRow
                                key={rowIndex}
                                debug={DEBUG}
                                items={row.items}
                                columnCount={columnCount}
                                customMarkings={customMarkings}
                                miniCalendarAspectRatio={calendarAspectRatio}
                                fontSize={fontSize}
                                prependZero={prependZero}
                                startWeekOnSundays={startWeekOnSundays}
                                showWeekColumn={showWeekColumn}
                                themeName={themeName}
                                gapBetweenColumnsInMm={GAP_BETWEEN_COLUMNS_IN_MM}
                                contentWidthInMm={CONTENT_WIDTH_MM}
                                marginTopInMm={marginTopInMm}
                                marginBottomInMm={marginBottomInMm}
                            />

                        );
                    }

                    return null;
                })}
            </View>

        </Page>
    );
};
